import { createRouter, createWebHistory, type RouteRecordRaw, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { Role } from '@/types/auth.types'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    roles?: Role[]
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { requiresAuth: false, title: 'Connexion...' },
  },
  {
    path: '/auth/login',
    name: 'AuthLogin',
    component: () => import('@/views/AuthLoginView.vue'),
    meta: { requiresAuth: false, title: 'Connexion' },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Tableau de bord',
        },
      },
      {
        path: 'services',
        name: 'Services',
        component: () => import('@/views/ServicesView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Services',
        },
      },
      {
        path: 'services/new',
        name: 'ServiceCreate',
        component: () => import('@/views/ServiceCreateView.vue'),
        meta: { requiresAuth: true, roles: ['infra-operator'], title: 'Nouveau service' },
      },
      {
        path: 'services/:id/edit',
        name: 'ServiceEdit',
        component: () => import('@/views/ServiceEditView.vue'),
        meta: { requiresAuth: true, roles: ['infra-operator'], title: 'Modifier le service' },
      },
      {
        path: 'services/:id',
        name: 'ServiceDetail',
        component: () => import('@/views/ServiceDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail service',
        },
      },
      {
        path: 'stacks',
        name: 'Stacks',
        component: () => import('@/views/StacksView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Stacks',
        },
      },
      {
        path: 'infra',
        name: 'Infra',
        component: () => import('@/views/InfraView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Infrastructure',
        },
      },
      {
        path: 'infra/targets',
        name: 'TargetsList',
        component: () => import('@/views/TargetsListView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Targets',
        },
      },
      {
        path: 'infra/targets/new',
        name: 'TargetCreate',
        component: () => import('@/views/TargetCreateView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['infra-operator'],
          title: 'Nouveau target',
        },
      },
      {
        path: 'infra/targets/:id/edit',
        name: 'TargetEdit',
        component: () => import('@/views/TargetEditView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['infra-operator'],
          title: 'Modifier le target',
        },
      },
      {
        path: 'infra/targets/:id/discovery',
        name: 'TargetDiscovery',
        component: () => import('@/views/TargetDiscoveryView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Discovery',
        },
      },
      {
        path: 'infra/targets/:id',
        name: 'TargetDetail',
        component: () => import('@/views/TargetDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail target',
        },
      },
      {
        path: 'infra/topology',
        name: 'InfraTopology',
        component: () => import('@/views/InfraTopologyView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Cartographie réseau',
        },
      },
      {
        path: 'infra/:id',
        name: 'InfraDetail',
        component: () => import('@/views/InfraDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'infra-operator'],
          title: 'Détail infrastructure',
        },
      },
      {
        path: 'deployments',
        name: 'DeploymentHistory',
        component: () => import('@/views/DeploymentHistoryView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Historique',
        },
      },
      {
        path: 'deployments/:id',
        name: 'DeploymentDetail',
        component: () => import('@/views/DeploymentDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail déploiement',
        },
      },
      {
        path: 'builds',
        name: 'Builds',
        component: () => import('@/views/BuildsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Builds',
        },
      },
      {
        path: 'builds/:id/logs',
        name: 'BuildLogs',
        component: () => import('@/views/BuildDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['deployer', 'releaser', 'infra-operator'],
          title: 'Logs de build',
        },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Paramètres',
        },
      },
      {
        path: 'audit',
        name: 'Audit',
        component: () => import('@/views/AuditView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Audit Trail',
        },
      },
      {
        path: 'applications',
        name: 'Applications',
        component: () => import('@/views/ApplicationsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Dépôts',
        },
      },
      {
        path: 'applications/:id',
        name: 'ApplicationDetail',
        component: () => import('@/views/ApplicationDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail application',
        },
      },
      {
        path: 'pipeline-monitor',
        name: 'PipelineMonitor',
        component: () => import('@/views/PipelineMonitorView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Supervision pipelines',
        },
      },
      {
        path: 'ci/:serviceName',
        name: 'CiDetail',
        component: () => import('@/views/CiDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'CI — Pipeline',
        },
      },
      {
        path: 'registrations',
        name: 'Registrations',
        component: () => import('@/views/RegistrationsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['infra-operator'],
          title: 'Enregistrements agents',
        },
      },
      {
        path: 'quality',
        name: 'QualityDashboard',
        component: () => import('@/views/quality/QualityDashboardView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Qualité de code',
        },
      },
      {
        path: 'quality/global-thresholds',
        name: 'QualityGlobalThresholds',
        component: () => import('@/views/quality/QualityGlobalThresholdsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Seuils globaux qualité',
        },
      },
      {
        path: 'quality/:serviceName',
        name: 'QualityService',
        component: () => import('@/views/quality/QualityServiceView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Qualité — service',
        },
      },
      // -- Couverture --
      {
        path: 'coverage',
        name: 'CoverageDashboard',
        component: () => import('@/views/coverage/CoverageDashboardView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Couverture de tests',
        },
      },
      {
        path: 'coverage/:serviceName',
        name: 'CoverageService',
        component: () => import('@/views/coverage/CoverageServiceView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Couverture — service',
        },
      },

      {
        path: 'environments',
        name: 'Environments',
        component: () => import('@/views/EnvironmentsDashboardView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Multi-environnements',
        },
      },
      {
        path: 'pipeline/:serviceId',
        name: 'PipelineList',
        component: () => import('@/views/PipelineListView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Pipeline — Runs',
        },
      },
      {
        path: 'pipeline/run/:id',
        name: 'PipelineRunDetail',
        component: () => import('@/views/PipelineRunDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail pipeline run',
        },
      },
      {
        path: 'registry',
        name: 'RegistryDashboard',
        component: () => import('@/views/registry/RegistryDashboardView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Registre Docker Hub',
        },
      },
      {
        path: 'private-registry',
        name: 'PrivateRegistry',
        component: () => import('@/views/private-registry/PrivateRegistryView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Registre privé',
        },
      },
      {
        path: 'private-registry/settings',
        name: 'PrivateRegistrySettings',
        component: () => import('@/views/private-registry/PrivateRegistrySettingsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['infra-operator'],
          title: 'Paramètres — Registre privé',
        },
      },
      {
        path: 'private-registry/:image(.*)',
        name: 'PrivateRepositoryDetail',
        component: () => import('@/views/private-registry/PrivateRepositoryDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Dépôt — Registre privé',
        },
      },
      {
        path: 'registry/settings',
        name: 'RegistrySettings',
        component: () => import('@/views/registry/RegistrySettingsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['infra-operator'],
          title: 'Paramètres — Registre',
        },
      },
      {
        path: 'registry/:namespace/:repository',
        name: 'RepositoryDetail',
        component: () => import('@/views/registry/RepositoryDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail dépôt',
        },
      },

      // -- Monitoring --
      {
        path: 'monitoring',
        name: 'MonitoringOverview',
        component: () => import('@/views/monitoring/MonitoringOverviewView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Monitoring',
        },
      },
      {
        path: 'monitoring/devices',
        name: 'MonitoringDevices',
        component: () => import('@/views/monitoring/MonitoringDevicesView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Équipements',
        },
      },
      {
        path: 'monitoring/devices/:id',
        name: 'MonitoringDeviceDetail',
        component: () => import('@/views/monitoring/MonitoringDeviceDetailView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Détail équipement',
        },
      },
      {
        path: 'monitoring/alerts',
        name: 'MonitoringAlerts',
        component: () => import('@/views/monitoring/MonitoringAlertsView.vue'),
        meta: {
          requiresAuth: true,
          roles: ['viewer', 'deployer', 'releaser', 'infra-operator'],
          title: 'Alertes monitoring',
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { requiresAuth: false, title: 'Page introuvable' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_, __, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  },
})

// Guard global
router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  // Mise à jour du titre de la page
  if (to.meta.title) {
    document.title = `${to.meta.title} — Beewize Harbor`
  }

  // Routes publiques
  if (to.meta.requiresAuth === false) {
    next()
    return
  }

  // Initialisation session au premier chargement
  if (!authStore.isInitialized) {
    await authStore.initFromSession()
  }

  // Vérification authentification
  if (!authStore.isAuthenticated) {
    next({ name: 'AuthLogin', query: { redirect: to.fullPath } })
    return
  }

  // Vérification des rôles requis
  const requiredRoles = to.meta.roles as Role[] | undefined
  if (requiredRoles && requiredRoles.length > 0) {
    if (!authStore.hasAnyRole(requiredRoles)) {
      // Import dynamique pour éviter la dépendance circulaire
      const { useNotificationsStore } = await import('@/stores/notifications')
      const notifStore = useNotificationsStore()
      notifStore.pushError('Accès refusé — rôle insuffisant pour cette page')
      next({ name: 'Dashboard' })
      return
    }
  }

  next()
})

export default router
